import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import {
  getPasswordStrength,
  getPasswordStrengthHints,
  PASSWORD_STRENGTH_LABELS,
} from "lib/auth/passwordRules";

// Theme colour token for the filled bars at each strength score (index = score).
const SCORE_COLORS = [
  "sysDestructiveDefault",
  "sysDestructiveDefault",
  "sysWarningDefault",
  "sysSuccessDefault",
] as const;

// A single bar. Defaults to the neutral track; `data-filled` swaps it to the passed-in active colour.
const Bar = styled.div<{ activeColor: string }>(({ activeColor, theme }) => ({
  flex: 1,
  height: 4,
  borderRadius: 2,
  backgroundColor: theme.colors.sysBorderDecorative,
  transition: "background-color 120ms ease",
  "&[data-filled]": { backgroundColor: activeColor },
}));

const HintList = styled.ul({ margin: 0, paddingLeft: 18 });

// Strength indicator shown under a "set a password" field. Renders nothing until the user types.
// The register/reset forms disable submit until the password reaches Medium; while it's below that
// we list exactly what's still missing (length / character variety) so the user knows how to fix it.
export function PasswordStrengthMeter({ password }: { password: string }) {
  const theme = useTheme();

  if (!password) return null;

  const { score, label } = getPasswordStrength(password);
  const activeColor = theme.colors[SCORE_COLORS[score] ?? "sysDestructiveDefault"];
  const hints = getPasswordStrengthHints(password);

  return (
    <Flex column gap={4} role="status" aria-live="polite">
      <Flex gap={4} aria-hidden>
        {PASSWORD_STRENGTH_LABELS.map((_, index) => (
          <Bar key={index} activeColor={activeColor} data-filled={index <= score || undefined} />
        ))}
      </Flex>
      <Text description color="sysTextSecondary">
        Password strength: {label}
      </Text>
      {hints.length > 0 && (
        <HintList>
          {hints.map((hint) => (
            <Text key={hint} as="li" description color="sysTextSecondary">
              {hint}
            </Text>
          ))}
        </HintList>
      )}
    </Flex>
  );
}
